import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { BoxDashboardPaginatedBlock } from "@/modules/boxDashboard/components/boxDashboardPaginatedBlock"
import { LoadingBlock } from "@/modules/boxDashboard/components/loadingBlock"
import {
  INACTIVE_MEMBERS_PAGE_SIZE,
  useInactiveMembers,
} from "@/modules/boxDashboard/modules/InactiveMembers/hooks/useInactiveMembers"
import {
  createInactiveMembersBlockModel,
  type InactiveMemberModel,
} from "@/modules/boxDashboard/modules/InactiveMembers/models/inactiveMembersModel"
import { ProfileAvatar } from "@/modules/profile/common/components/profileAvatar"
import attendeesEmptyIllustration from "@images/empty/attendees_empty.svg"
import { word } from "@modules/core/utils/i18n"
import { useMemberSideBar } from "@modules/members/hooks/useMemberSidebar"
import { CircleHelp } from "lucide-react"
import { startTransition, useState } from "react"

export const InactiveMembersBlock = () => {
  const [currentPage, setCurrentPage] = useState(0)
  const [isExpanded, setIsExpanded] = useState(false)

  const { members, totalCount, isFetching } = useInactiveMembers(currentPage)
  const { navigateToMemberDetailsInfo } = useMemberSideBar()

  const model = createInactiveMembersBlockModel({
    members,
    totalCount,
    currentPage,
    pageSize: INACTIVE_MEMBERS_PAGE_SIZE,
    isExpanded,
  })

  const handleMemberClick = (memberId: string) => {
    navigateToMemberDetailsInfo(memberId, {
      replaceAllComponents: true,
    })
  }

  const handleShowMore = () => {
    setIsExpanded(true)
  }

  const handleShowLess = () => {
    startTransition(() => {
      setIsExpanded(false)
      setCurrentPage(0)
    })
  }

  const header = (
    <div className="flex shrink-0 items-center justify-between py-0 pr-4 pl-0">
      <div className="flex flex-col gap-1 pt-6 pr-4 pb-0 pl-6">
        <div className="flex items-center gap-2">
          <Text fontStyle="HM" className="text-textDefault-heading">
            {word("boxDashboard.inactiveMembers.title")}
          </Text>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger
                render={<CircleHelp className="size-6 text-textOnSurface-subtle hover:text-textOnSurface-muted" />}
              />
              <TooltipContent side="bottom" align="start" className="flex-col max-w-60 rounded-xl p-0 shadow-lg">
                <div className="flex w-full items-center gap-1 border-b border-interactive-border-neutral-default p-4">
                  <CircleHelp className="size-6 text-textOnSurface-subtle" />
                  <Text fontStyle="LM">{word("boxDashboard.inactiveMembers.title")}</Text>
                </div>
                <div className="p-4 pt-2">
                  <Text fontStyle="BM" className="wrap-break-word whitespace-break-spaces">
                    {word("boxDashboard.inactiveMembers.tooltip")}
                  </Text>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Text fontStyle="BM" className="text-textOnSurface-muted">
          {word("boxDashboard.inactiveMembers.subtitle")}
        </Text>
      </div>
    </div>
  )

  return (
    <BoxDashboardPaginatedBlock
      header={header}
      isEmpty={model.isEmpty}
      isLoading={isFetching}
      loadingComponent={<LoadingBlock className="mx-6 mt-6 h-90" />}
      emptyState={<EmptyState />}
      isExpanded={isExpanded}
      showShowMore={model.showShowMore}
      showShowLess={model.showShowLess}
      showPagination={model.showPagination}
      onShowMore={handleShowMore}
      onShowLess={handleShowLess}
      totalPages={model.totalPages}
      currentPage={currentPage}
      onPageChange={(page) => {
        startTransition(() => {
          setCurrentPage(page)
        })
      }}
    >
      <div className="grid grid-cols-2 gap-x-1 gap-y-3 px-6">
        {model.members.map((member) => (
          <MemberCard key={member.memberId} member={member} onClick={() => {
            handleMemberClick(member.memberId)
          }} />
        ))}
      </div>
    </BoxDashboardPaginatedBlock>
  )
}

const EmptyState = () => {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2">
      <img src={attendeesEmptyIllustration} alt="" className="h-43.75 w-74.5" />
      <Text fontStyle="LL" className="text-center">
        {word("boxDashboard.inactiveMembers.empty.title")}
      </Text>
    </div>
  )
}

const MemberCard = ({ member, onClick }: { member: InactiveMemberModel; onClick: () => void }) => {
  return (
    <button
      className="flex min-w-45 cursor-pointer flex-col overflow-hidden rounded-xl border-2 border-interactive-border-neutral-disabled bg-interactive-background-neutral-faded-highlighted text-left hover:border-interactive-background-neutral-highlighted"
      onClick={onClick}
    >
      {/* Top section: Avatar, Name, Offer, Badge */}
      <div className="flex w-full items-center justify-between bg-linear-to-r from-surface-background-float from-0% to-interactive-background-neutral-faded-highlighted to-150% p-2">
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <ProfileAvatar className="size-6 shrink-0" profile={{ avatar: member.avatar, username: member.fullName }} />
          <div className="flex min-w-0 flex-1 flex-col">
            <Text fontStyle="LM" className="truncate">
              {member.fullName}
            </Text>
            <Text fontStyle="LS" className="truncate text-textOnSurface-muted">
              {member.purchaseName ?? "\u00A0"}
            </Text>
          </div>
        </div>
        <div className="flex items-center justify-center rounded-full bg-interactive-background-neutral-faded-highlighted px-1.5">
          <Text fontStyle="BXS" className="">
            {member.inactiveDaysLabel}
          </Text>
        </div>
      </div>

      {/* Bottom section: Dates */}
      <div className="flex w-full gap-1 bg-surface-background p-2">
        <div className="flex flex-1 flex-col">
          <Text fontStyle="LXS" className="text-textOnSurface-muted">
            {word("boxDashboard.inactiveMembers.memberSince")}
          </Text>
          <Text fontStyle="BXS" className="text-textOnSurface-subtle">
            {member.memberSince ?? "-"}
          </Text>
        </div>
        <div className="flex flex-1 flex-col">
          <Text fontStyle="LXS" className="text-textOnSurface-muted">
            {word("boxDashboard.inactiveMembers.lastSession")}
          </Text>
          <Text fontStyle="BXS" className="text-textOnSurface-subtle">
            {member.lastSession ?? "-"}
          </Text>
        </div>
      </div>
    </button>
  )
}
